import React, { useState } from "react";
import { Box, Button, Stack, Typography, Alert } from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { PermissionTable } from "../components/permissions/PermissionTable";
import { PermissionForm } from "../components/permissions/PermissionForm";
import { PermissionStructureEditor } from "../components/permissions/PermissionStructureEditor";
import { permissionService } from "../services/permissionService";
import type { Permission } from "@/types/auth";

/**
 * Página de gestión de permisos
 * CRUD completo de permisos con estructura JSON jerárquica
 *
 * REGLAS APLICADAS:
 * - Solo <Stack> para layout (NO Grid)
 * - Componentes con size="small"
 * - Material-UI v7+ con tema MásXPieza
 * - Máximo 500 líneas
 */

export function PermissionsPage(): React.ReactElement {
  const [formOpen, setFormOpen] = useState(false);
  const [structureEditorOpen, setStructureEditorOpen] = useState(false);
  const [selectedPermission, setSelectedPermission] =
    useState<Permission | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const refreshData = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  // Crear nuevo permiso
  const handleCreate = async (
    permissionData: Omit<
      Permission,
      "id" | "fecha_creacion" | "fecha_actualizacion"
    >,
  ) => {
    try {
      setError(null);

      await permissionService.createPermission({
        nombre: permissionData.nombre,
        descripcion: permissionData.descripcion || "",
        estructura_permisos: permissionData.estructura_permisos,
        activo: permissionData.activo,
      });

      refreshData();
    } catch (err) {
      throw new Error(
        err instanceof Error ? err.message : "Error creando permiso",
      );
    }
  };

  // Editar permiso existente
  const handleEdit = (permission: Permission) => {
    setSelectedPermission(permission);
    setFormOpen(true);
  };

  const handleUpdate = async (
    permissionData: Omit<
      Permission,
      "id" | "fecha_creacion" | "fecha_actualizacion"
    >,
  ) => {
    if (!selectedPermission) return;

    try {
      setError(null);

      await permissionService.updatePermission(selectedPermission.id, {
        nombre: permissionData.nombre,
        descripcion: permissionData.descripcion || "",
        estructura_permisos: permissionData.estructura_permisos,
        activo: permissionData.activo,
      });

      refreshData();
    } catch (err) {
      throw new Error(
        err instanceof Error ? err.message : "Error actualizando permiso",
      );
    }
  };

  // Eliminar permiso
  const handleDelete = async (permissionId: string) => {
    try {
      setError(null);

      await permissionService.deletePermission(permissionId);

      refreshData();
    } catch (err) {
      throw new Error(
        err instanceof Error ? err.message : "Error eliminando permiso",
      );
    }
  };

  // Cambiar estado activo/inactivo
  const handleToggleStatus = async (
    permissionId: string,
    newStatus: boolean,
  ) => {
    try {
      setError(null);

      await permissionService.togglePermissionStatus(permissionId, newStatus);

      refreshData();
    } catch (err) {
      throw new Error(
        err instanceof Error
          ? err.message
          : "Error cambiando estado del permiso",
      );
    }
  };

  // Abrir editor de estructura
  const handleViewStructure = (permission: Permission) => {
    setSelectedPermission(permission);
    setStructureEditorOpen(true);
  };

  // Guardar estructura de permisos
  const handleSaveStructure = async (
    permissionId: string,
    estructura: Record<string, any>,
  ) => {
    try {
      setError(null);

      await permissionService.updatePermission(permissionId, {
        estructura_permisos: estructura,
      });

      refreshData();
    } catch (err) {
      throw new Error(
        err instanceof Error
          ? err.message
          : "Error guardando estructura de permisos",
      );
    }
  };

  return (
    <Stack spacing={3}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Gestión de Permisos
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Administración de permisos granulares por ruta, menú y función
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => {
            setSelectedPermission(null);
            setFormOpen(true);
          }}
        >
          Crear Permiso
        </Button>
      </Stack>

      {/* Errores globales */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabla de permisos */}
      <PermissionTable
        key={refreshTrigger}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onViewStructure={handleViewStructure}
        onToggleStatus={handleToggleStatus}
      />

      {/* Formulario de crear/editar */}
      <PermissionForm
        open={formOpen}
        permission={selectedPermission}
        onClose={() => {
          setFormOpen(false);
          setSelectedPermission(null);
        }}
        onSave={selectedPermission ? handleUpdate : handleCreate}
      />

      {/* Editor de estructura de permisos */}
      <PermissionStructureEditor
        open={structureEditorOpen}
        permission={selectedPermission}
        onClose={() => {
          setStructureEditorOpen(false);
          setSelectedPermission(null);
        }}
        onSave={handleSaveStructure}
      />
    </Stack>
  );
}
